/**
 * Journées de formation lues dans un texte libre.
 *
 * Le client écrit ses choix de dates comme il parle : « SSCT du 23 septembre »,
 * « formation économique les 12, 13 et 14/11/2026 », « Référent harcèlement :
 * 3 octobre ». Ressaisir ces dates dans la planification est long et
 * fautif ; on les lit à sa place, comme on lit déjà les élus dans son mail
 * (participantsDepuisEmail).
 *
 * Deux ancres : une DATE (jour + mois, au moins), et une FORMATION reconnue
 * — un code du catalogue, un intitulé, ou un mot qui ne trompe pas (SSCT,
 * économique, harcèlement…). Une ligne qui porte des dates mais ne nomme
 * pas de formation hérite de la dernière formation nommée au-dessus :
 * « SSCT : » puis « 23 et 24 septembre » sur la ligne suivante.
 *
 * Le résultat est une proposition, mise en brouillon dans le panneau de
 * planification ; rien n'est écrit avant « Enregistrer la planification ».
 */

const MOIS = {
  janvier: 1, janv: 1, jan: 1,
  fevrier: 2, fev: 2, feb: 2,
  mars: 3,
  avril: 4, avr: 4,
  mai: 5,
  juin: 6,
  juillet: 7, juil: 7,
  aout: 8,
  septembre: 9, sept: 9, sep: 9,
  octobre: 10, oct: 10,
  novembre: 11, nov: 11,
  decembre: 12, dec: 12,
};

const normaliser = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

const deuxChiffres = (n) => String(n).padStart(2, '0');

function iso(annee, mois, jour) {
  const d = new Date(annee, mois - 1, jour);
  if (d.getFullYear() !== annee || d.getMonth() !== mois - 1 || d.getDate() !== jour) return null;
  return `${annee}-${deuxChiffres(mois)}-${deuxChiffres(jour)}`;
}

/**
 * L'année quand le texte ne la dit pas : celle en cours, sauf si la date
 * serait déjà passée de plus de deux mois — un client qui écrit « 23
 * septembre » en novembre parle de l'an prochain.
 */
function anneeParDefaut(mois, jour, aujourdhui) {
  const annee = aujourdhui.getFullYear();
  const d = new Date(annee, mois - 1, jour);
  const limite = new Date(aujourdhui);
  limite.setMonth(limite.getMonth() - 2);
  return d < limite ? annee + 1 : annee;
}

function lireAnnee(brut) {
  if (!brut) return null;
  const n = Number(brut);
  return n < 100 ? 2000 + n : n;
}

function joursEntre(debut, fin) {
  const jours = [];
  const d = new Date(`${debut}T00:00:00`);
  const f = new Date(`${fin}T00:00:00`);
  if (Number.isNaN(d.getTime()) || Number.isNaN(f.getTime()) || f < d) return [debut];
  // Une plage de plus de dix jours n'est pas une session : on garde les bornes.
  if ((f - d) / 86400000 > 10) return [debut, fin];
  for (let x = new Date(d); x <= f; x.setDate(x.getDate() + 1)) {
    jours.push(`${x.getFullYear()}-${deuxChiffres(x.getMonth() + 1)}-${deuxChiffres(x.getDate())}`);
  }
  return jours;
}

const RE_MOIS_MOT = '(janvier|janv|jan|f[ée]vrier|f[ée]v|feb|mars|avril|avr|mai|juin|juillet|juil|ao[ûu]t|septembre|sept|sep|octobre|oct|novembre|nov|d[ée]cembre|d[ée]c)';

/**
 * Toutes les dates d'une ligne, en ISO, dédoublonnées et triées.
 *
 * Formes lues : 23/09/2026, 23/09/26, 23/09, 23 septembre 2026, 23 septembre,
 * « du 23 au 25 septembre », « 23, 24 et 25 septembre », « 23 et 24/09/2026 ».
 */
export function datesDansTexte(texte, aujourdhui = new Date()) {
  const ligne = normaliser(texte).replace(/1er\b/g, '1');
  const trouvees = new Set();

  // 1. Jour(s) + mois en toutes lettres : « 23 septembre 2026 », « du 23 au
  //    25 septembre », « 23, 24 et 25 septembre 2026 ».
  const reMot = new RegExp(
    `((?:\\d{1,2}\\s*(?:,|et|au|-|–|/)?\\s*)+)\\s*${RE_MOIS_MOT}\\.?\\s*(\\d{4})?`,
    'g'
  );
  for (const m of ligne.matchAll(reMot)) {
    const mois = MOIS[normaliser(m[2]).replace(/[^a-z]/g, '')];
    if (!mois) continue;
    const annee = lireAnnee(m[3]);
    const nombres = m[1].match(/\d{1,2}/g) || [];
    const plage = /\bau\b|[-–]/.test(m[1]) && nombres.length === 2;
    const joursIso = nombres
      .map((n) => {
        const jour = Number(n);
        if (jour < 1 || jour > 31) return null;
        return iso(annee || anneeParDefaut(mois, jour, aujourdhui), mois, jour);
      })
      .filter(Boolean);
    if (plage && joursIso.length === 2) {
      joursEntre(joursIso[0], joursIso[1]).forEach((j) => trouvees.add(j));
    } else {
      joursIso.forEach((j) => trouvees.add(j));
    }
  }

  // 2. Forme numérique : 23/09/2026, 23/09/26, 23/09 — et « 23 et 24/09 »
  //    (jours devant, mois porté par le dernier).
  const reNum = /((?:\d{1,2}\s*(?:,|et|au|-|–)\s*)*)(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?/g;
  for (const m of ligne.matchAll(reNum)) {
    const mois = Number(m[3]);
    if (mois < 1 || mois > 12) continue;
    const annee = lireAnnee(m[4]);
    const jours = [...(m[1].match(/\d{1,2}/g) || []), m[2]].map(Number);
    const plage = /\bau\b|[-–]/.test(m[1]) && jours.length === 2;
    const joursIso = jours
      .filter((j) => j >= 1 && j <= 31)
      .map((j) => iso(annee || anneeParDefaut(mois, j, aujourdhui), mois, j))
      .filter(Boolean);
    if (plage && joursIso.length === 2) {
      joursEntre(joursIso[0], joursIso[1]).forEach((j) => trouvees.add(j));
    } else {
      joursIso.forEach((j) => trouvees.add(j));
    }
  }

  return [...trouvees].sort();
}

/* ------------------------------------------------------------------ */
/* Formations                                                          */
/* ------------------------------------------------------------------ */

/**
 * Mots qui désignent une famille de formation sans ambiguïté. Un candidat
 * (ligne du devis ou fiche du catalogue) porte la famille que son code ou
 * son intitulé contient ; une ligne de texte la porte de même.
 */
const FAMILLES = [
  { cle: 'ssct', motif: /\bssct\b|\bsst\b|sante\s*(?:,|et|&)?\s*securite|\bcsst\b/ },
  { cle: 'economique', motif: /economi/ },
  { cle: 'harcelement', motif: /harcel|referent/ },
  { cle: 'tresorier', motif: /tresor/ },
  { cle: 'secretaire', motif: /secretaire/ },
  { cle: 'bdese', motif: /\bbdese?\b/ },
  { cle: 'president', motif: /president/ },
];

function famillesDe(texte) {
  const t = normaliser(texte);
  return FAMILLES.filter((f) => f.motif.test(t)).map((f) => f.cle);
}

const echapper = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Candidats à reconnaître : les lignes du devis d'abord (c'est ce qui est
 * vendu), puis le catalogue pour ce qui n'y figure pas.
 */
export function candidatsFormations({ lignesDevis = [], catalogue = [] } = {}) {
  const candidats = [];
  for (const l of lignesDevis) {
    const intitule = l.formation_intitule || l.libelle || '';
    candidats.push({
      formationId: l.formation_id ? String(l.formation_id) : '',
      code: l.formation_code || '',
      intitule,
      groupeLibelle: l.groupe_libelle || '',
      format: l.format || '',
      calendrierId: l.calendrier_id ? String(l.calendrier_id) : '',
      familles: famillesDe(`${l.formation_code || ''} ${intitule} ${l.libelle || ''}`),
      vendue: true,
    });
  }
  for (const f of catalogue) {
    if (candidats.some((c) => c.formationId && c.formationId === String(f.id))) continue;
    candidats.push({
      formationId: String(f.id),
      code: f.code || '',
      intitule: f.intitule || '',
      groupeLibelle: '',
      format: '',
      calendrierId: '',
      familles: famillesDe(`${f.code || ''} ${f.intitule || ''}`),
      vendue: false,
    });
  }
  return candidats;
}

/** La formation que nomme cette ligne, parmi les candidats, ou null. */
export function formationDansTexte(ligne, candidats) {
  const t = normaliser(ligne);
  if (!t.trim()) return null;

  // Un code du catalogue ou un intitulé complet : sans appel.
  for (const c of candidats) {
    if (c.code && new RegExp(`(^|[^a-z0-9])${echapper(normaliser(c.code))}([^a-z0-9]|$)`).test(t)) {
      return c;
    }
  }
  for (const c of candidats) {
    const intitule = normaliser(c.intitule).trim();
    if (intitule.length >= 6 && t.includes(intitule)) return c;
  }

  // Sinon un mot de famille : la ligne du devis de cette famille, sinon la
  // fiche du catalogue.
  const familles = famillesDe(t);
  if (!familles.length) return null;
  const parFamille = (liste) =>
    liste.find((c) => c.familles.some((f) => familles.includes(f))) || null;
  return parFamille(candidats.filter((c) => c.vendue)) || parFamille(candidats);
}

/* ------------------------------------------------------------------ */
/* Assemblage                                                          */
/* ------------------------------------------------------------------ */

const jourLocal = (valeur) => {
  if (!valeur) return '';
  const s = String(valeur);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${deuxChiffres(d.getMonth() + 1)}-${deuxChiffres(d.getDate())}`;
};

/**
 * La session du calendrier qui porte cette formation à l'une de ces dates,
 * ou null. `calendrier` : les propositions de la planification
 * (v_calendrier + formation_id).
 */
export function sessionCalendrierPour(formation, jours, calendrier = []) {
  if (!formation || !jours.length) return null;
  return (
    calendrier.find((c) => {
      const memeFormation =
        (formation.formationId && String(c.formation_id) === formation.formationId) ||
        (formation.code && c.formation_code === formation.code);
      if (!memeFormation) return false;
      const debut = jourLocal(c.date_debut);
      const fin = jourLocal(c.date_fin) || debut;
      return jours.some((j) => j >= debut && j <= fin);
    }) || null
  );
}

/**
 * Lit un texte et en tire des journées à planifier.
 *
 * @returns {Array<{ formation, jours, calendrierId, intitule, groupeLibelle, extrait }>}
 */
export function sessionsDepuisTexte(
  texte,
  { lignesDevis = [], catalogue = [], calendrier = [], aujourdhui = new Date() } = {}
) {
  const contenu = String(texte || '').replace(/\r\n?/g, '\n');
  if (!contenu.trim()) return [];
  const candidats = candidatsFormations({ lignesDevis, catalogue });
  if (!candidats.length) return [];

  const propositions = [];
  let derniere = null;

  for (const brute of contenu.split('\n')) {
    const ligne = brute.trim();
    if (!ligne) continue;
    // Une ligne d'élu (adresse email) n'est pas une ligne de dates : la date
    // d'une signature de mail ou d'un en-tête « Envoyé : » ne compte pas.
    if (/@/.test(ligne) || /^(de|à|a|cc|envoy[ée]|sent|from|to)\s*:/i.test(ligne)) continue;

    const formation = formationDansTexte(ligne, candidats);
    if (formation) derniere = formation;

    const jours = datesDansTexte(ligne, aujourdhui);
    if (!jours.length) continue;

    const retenue = formation || derniere;
    if (!retenue) continue;

    // Même formation sur deux lignes qui se suivent : une seule session.
    const precedente = propositions[propositions.length - 1];
    if (precedente && precedente.formation === retenue && !formation) {
      precedente.jours = [...new Set([...precedente.jours, ...jours])].sort();
      precedente.extrait += ` / ${ligne}`;
      continue;
    }

    propositions.push({
      formation: retenue,
      jours,
      intitule: retenue.intitule,
      groupeLibelle: retenue.groupeLibelle || '',
      calendrierId: '',
      extrait: ligne.length > 90 ? `${ligne.slice(0, 87)}…` : ligne,
    });
  }

  for (const p of propositions) {
    const s = sessionCalendrierPour(p.formation, p.jours, calendrier);
    p.calendrierId = s ? String(s.calendrier_id) : '';
    p.calendrier = s || null;
  }
  return propositions;
}

export default sessionsDepuisTexte;
